import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Button } from "../../components/ui/button";
import {
  Users, Download, TrendingUp, ChevronDown, ChevronRight, Loader2,
  Percent, DollarSign, Building2
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ReportProvvigioniPanel = () => {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  const headers = { Authorization: `Bearer ${localStorage.getItem("sa_token")}` };

  const fetchReport = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/superadmin/venditori/report`, { headers });
      setReport(res.data);
    } catch {
      toast.error("Errore caricamento report");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  const exportExcel = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/superadmin/venditori/report/export`, {
        headers, responseType: "blob"
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `report_provvigioni_${new Date().toISOString().slice(0, 10)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Report scaricato!");
    } catch {
      toast.error("Errore download report");
    } finally { setExporting(false); }
  };

  if (loading) return (
    <div className="flex items-center justify-center py-12">
      <Loader2 className="w-5 h-5 animate-spin text-indigo-500" />
    </div>
  );

  if (!report) return null;

  return (
    <div className="space-y-6" data-testid="report-provvigioni-panel">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-black tracking-tight text-slate-900">Report Provvigioni</h2>
          <p className="text-sm text-slate-500 mt-0.5">
            {report.venditori.length} venditori &middot; Generato: {new Date(report.generated_at).toLocaleString("it-IT")}
          </p>
        </div>
        <Button onClick={exportExcel} disabled={exporting}
          className="rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white hover:text-white font-semibold"
          data-testid="export-provvigioni-btn">
          <Download className="h-4 w-4 mr-1.5" />
          {exporting ? "Scaricando..." : "Esporta Excel"}
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white rounded-2xl p-5 border border-slate-200 shadow-sm">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center">
              <Users className="h-5 w-5 text-indigo-600" />
            </div>
            <div>
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Venditori Attivi</p>
              <p className="text-2xl font-black text-slate-900" data-testid="venditori-count">{report.venditori.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-2xl p-5 border border-slate-200 shadow-sm">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
              <TrendingUp className="h-5 w-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Provvigioni Mensili</p>
              <p className="text-2xl font-black text-emerald-700" data-testid="totale-provvigioni">
                {report.totale_provvigioni_mensili.toFixed(2)}€
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-2xl p-5 border border-slate-200 shadow-sm">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
              <Building2 className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Clienti Totali</p>
              <p className="text-2xl font-black text-slate-900" data-testid="clienti-totali">
                {report.venditori.reduce((acc, v) => acc + v.clienti_count, 0)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {report.venditori.length === 0 ? (
        <div className="bg-white rounded-2xl p-10 text-center border border-slate-200">
          <Users className="h-10 w-10 text-slate-200 mx-auto mb-3" />
          <p className="text-slate-400 text-sm">Nessun venditore con clienti associati</p>
        </div>
      ) : (
        <div className="space-y-3">
          {report.venditori.map(v => (
            <div key={v.venditore_id} className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm"
              data-testid={`report-venditore-${v.venditore_id}`}>
              <div className="p-5 flex items-center justify-between cursor-pointer hover:bg-slate-50/50 transition-colors"
                onClick={() => setExpandedId(expandedId === v.venditore_id ? null : v.venditore_id)}>
                <div className="flex items-center gap-4 flex-1 min-w-0">
                  <div className="w-10 h-10 rounded-xl bg-indigo-100 text-indigo-700 flex items-center justify-center text-sm font-bold">
                    {v.nome?.charAt(0)?.toUpperCase() || "V"}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="font-bold text-slate-900 truncate">{v.nome}</h3>
                      <span className="text-[10px] px-2 py-0.5 rounded-full font-bold bg-slate-100 text-slate-500 font-mono">{v.codice}</span>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-slate-500 mt-0.5">
                      <span className="flex items-center gap-1">
                        {v.tipo_provvigione === "percentuale" ? <Percent className="h-3 w-3" /> : <DollarSign className="h-3 w-3" />}
                        {v.valore_provvigione}{v.tipo_provvigione === "percentuale" ? "%" : "€"}
                      </span>
                      <span className="flex items-center gap-1"><Users className="h-3 w-3" />{v.clienti_count} clienti</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <p className="text-xl font-black text-emerald-700">{v.provvigione_totale.toFixed(2)}€</p>
                    <p className="text-[10px] text-slate-400">provvigione/mese</p>
                  </div>
                  {expandedId === v.venditore_id ? <ChevronDown className="h-4 w-4 text-slate-400" /> : <ChevronRight className="h-4 w-4 text-slate-400" />}
                </div>
              </div>

              {expandedId === v.venditore_id && v.clienti.length > 0 && (
                <div className="border-t border-slate-100">
                  <div className="px-5 py-2 bg-slate-50 grid grid-cols-5 gap-2 text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                    <span>Cliente</span>
                    <span>Pacchetti</span>
                    <span className="text-right">Costo/mese</span>
                    <span className="text-right">Provvigione</span>
                    <span className="text-center">Stato</span>
                  </div>
                  {v.clienti.map(c => (
                    <div key={c.societa_id} className="px-5 py-3 grid grid-cols-5 gap-2 items-center border-t border-slate-50 text-sm">
                      <span className="font-medium text-slate-800 truncate">{c.nome}</span>
                      <span className="text-xs text-slate-500 truncate">{c.pacchetti.join(", ")}</span>
                      <span className="text-right font-semibold text-slate-700">{c.costo_mensile.toFixed(2)}€</span>
                      <span className="text-right font-bold text-emerald-700">{c.provvigione.toFixed(2)}€</span>
                      <span className="text-center">
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${c.attivo ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-400'}`}>
                          {c.attivo ? "Attivo" : "Inattivo"}
                        </span>
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ReportProvvigioniPanel;
